import { useQuery } from "@tanstack/react-query";

import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import { LanguageSettingsType } from "@/model/Application/LanguageSettings";
import {
	BrandingSettingsType,
	GeneralFormData,
} from "@/model/Application/Settings/FormModels";
import {
	LabParameter,
	LabSettings,
} from "@/model/Application/Settings/LabSettings";
import { SystemSettingsResponse } from "@/model/Application/Settings/RequestTypes";
import {
	ActiveDirectorySettings,
	AzureAdGroupSyncLogEntry,
	ProceduresSettings,
	ServerEmailSettingsResponse,
	ServerPdfConversionSettingsResponse,
	ServerWordConvertorSettingsResponse,
	SystemInformation,
	TaskSettingsResponse,
} from "@/model/Application/Settings/SettingsResponseTypes";
import { Timezones } from "@/model/Application/Settings/Timezones";
import { MaterialHandlingSettings } from "@/model/MaterialHandling/MaterialHandlingSettings";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";

export const useFetchActiveDirectorySettings = () => {
	return useQuery<ActiveDirectorySettings, any>({
		queryKey: [SettingsQueryKeys.activeDirectorySettings],
		queryFn: SATOSPortal.GetActiveDirectorySettings,
	});
};

export const useFetchOrganizationSettings = () => {
	return useQuery<GeneralFormData, any>({
		queryKey: [
			ApplicationQueryKeys.user,
			SettingsQueryKeys.settings,
			SettingsQueryKeys.organization,
		],
		queryFn: SATOSPortal.GetOrganizationSettings,
	});
};

export const useFetchBrandingSettings = () => {
	return useQuery<BrandingSettingsType, any>({
		queryKey: [SettingsQueryKeys.brandingSettings],
		queryFn: SATOSPortal.GetBrandingSettings,
	});
};

export const useFetchLanguageSettings = (
	orderBy: string,
	direction: string,
) => {
	return useQuery<LanguageSettingsType[], any>({
		queryFn: () =>
			SATOSPortal.GetLanguageSettings(
				`?orderBy=${orderBy}&direction=${direction}`,
			),
		queryKey: [SettingsQueryKeys.languageSettings, orderBy, direction],
	});
};

export const useFetchLabParameters = () => {
	return useQuery<LabParameter[], any>({
		queryKey: [SettingsQueryKeys.labParameters],
		queryFn: SATOSPortal.GetLabParameters,
	});
};

export const useFetchLabSettings = () => {
	return useQuery<LabSettings, any>({
		queryKey: [SettingsQueryKeys.labSettings],
		queryFn: SATOSPortal.GetLabSettings,
	});
};

export const useFetchMaterialHandlingSettings = () => {
	return useQuery<MaterialHandlingSettings, any>({
		queryFn: () => SATOSPortal.GetMaterialHandlingSettings(),
		queryKey: [SettingsQueryKeys.materialHandlingSettings],
	});
};

export const useFetchProceduresSettings = () => {
	return useQuery<ProceduresSettings, any>({
		queryKey: [
			ApplicationQueryKeys.user,
			SettingsQueryKeys.settings,
			SettingsQueryKeys.procedures,
		],
		queryFn: SATOSPortal.GetProceduresSettings,
	});
};

export const useFetchTaskSettings = () => {
	return useQuery<TaskSettingsResponse, any>({
		queryKey: [SettingsQueryKeys.taskSettings],
		queryFn: SATOSPortal.GetTaskSettings,
	});
};

export const useFetchServerEmailSettings = () => {
	return useQuery<ServerEmailSettingsResponse, any>({
		queryKey: [SettingsQueryKeys.serverEmailSettings],
		queryFn: SATOSPortal.GetServerEmailSettings,
	});
};

export const useFetchServerPdfConversionSettings = () => {
	return useQuery<ServerPdfConversionSettingsResponse, any>({
		queryKey: [SettingsQueryKeys.serverPdfConversionSettings],
		queryFn: SATOSPortal.GetServerPdfConversionSettings,
	});
};

export const useFetchServerWordConvertorSettings = () => {
	return useQuery<ServerWordConvertorSettingsResponse, any>({
		queryKey: [SettingsQueryKeys.serverWordConvertorSettings],
		queryFn: SATOSPortal.GetServerWordConvertorSettings,
	});
};

export const useFetchSystemInformation = () => {
	return useQuery<SystemInformation, any>({
		queryKey: [SettingsQueryKeys.systemInformation],
		queryFn: () => SATOSPortal.GetSystemInformation(),
	});
};

export const useFetchSystemSettings = () => {
	return useQuery<SystemSettingsResponse, any>({
		queryKey: [SettingsQueryKeys.systemSettings],
		queryFn: () => SATOSPortal.GetSystemSettings(),
	});
};

export const useFetchAzureAdGroupSyncLogs = () => {
	return useQuery<AzureAdGroupSyncLogEntry[], any>({
		queryKey: [SettingsQueryKeys.azureAdGroupSyncLogs],
		queryFn: () => SATOSPortal.GetAzureAdGroupSyncLogs(),
		refetchInterval: 3000,
	});
};

export const useFetchTimezones = () => {
	return useQuery<Timezones[], any>({
		queryKey: [
			ApplicationQueryKeys.user,
			SettingsQueryKeys.settings,
			SettingsQueryKeys.timezones,
		],
		queryFn: SATOSPortal.GetTimezones,
	});
};

export class SettingsQueryKeys {
	static readonly settings = "Settings";
	static readonly activeDirectorySettings = "activeDirectorySettings";
	static readonly brandingSettings = "Branding Settings";
	static readonly organization = "Organization Settings";
	static readonly procedures = "Procedures Settings";
	static readonly labParameters = "labParameters";
	static readonly labSettings = "labSettings";
	static readonly languageSettings = "languageSettings";
	static readonly startPageImage = "Start Page Image";
	static readonly taskSettings = "taskSettings";
	static readonly timezones = "timezones";
	static readonly serverEmailSettings = "serverEmailSettings";
	static readonly serverWordConvertorSettings = "serverWordConvertorSettings";
	static readonly serverPdfConversionSettings = "serverPdfConversionSettings";
	static readonly materialHandlingSettings = "materialHandlingSettings";
	static readonly systemInformation = "systemInformation";
	static readonly systemSettings = "systemSettings";
	static readonly azureAdGroupSyncLogs = "azureAdGroupSyncLogs";
}
